import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaUserAstronaut, FaSearchLocation } from 'react-icons/fa';
import Header from 'components/Header';
import Footer from 'components/Footer';
import bgImage from '../../assets/img/pictures/tttt.jpg'; // Update the import path

const Join = () => {
  const options = [
    {
      title: "Join as Player",
      icon: <FaUserAstronaut className="h-12 w-12 text-blue-500" />,
      image: require("../../assets/img/pictures/WhatsApp Image 2025-03-02 at 15.40.43_a725256c.jpg"),
      description: "Start your professional football journey with us. Get access to top-tier training, professional coaches, and opportunities to showcase your talent.",
      benefits: [
        "Professional coaching from UEFA licensed trainers",
        "Access to state-of-the-art training facilities",
        "Opportunity to join competitive leagues",
        "Regular performance assessments",
        "Career guidance and development"
      ],
      additionalImages: [
        "https://images.unsplash.com/photo-1574629810360-7efbbe195018?ixlib=rb-4.0.3", // Training session
        "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?ixlib=rb-4.0.3", // Match play
      ],
      link: "/auth/register?type=player"
    },
    {
      title: "Join as Scout",
      icon: <FaSearchLocation className="h-12 w-12 text-green-500" />,
      image: require("../../assets/img/pictures/abouts us.jpg"),
      description: "Connect with emerging talent and help shape the future of football. Get exclusive access to player profiles and assessment tools.",
      benefits: [
        "Access to player database and statistics",
        "Invitation to exclusive scouting events",
        "Direct communication with academy staff",
        "Regular talent showcase events",
        "Professional scouting tools and resources"
      ],
      additionalImages: [
        "https://images.unsplash.com/photo-1547019431-9d2f0518bb5b?ixlib=rb-4.0.3", // Analytics
        "https://images.unsplash.com/photo-1574629810360-7efbbe195018?ixlib=rb-4.0.3", // Player assessment
      ],
      link: "/auth/register-scout?type=scout"
    }
  ];

  return (
    <div className="relative min-h-screen">
      <Header />
      
      {/* Background Image with Overlay */}
      <div 
        className="fixed inset-0 z-0 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.4)), url(${bgImage})`,
        }}
      />

      {/* Main Content */}
      <div className="relative z-10 pt-20">
        <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold text-white mb-4"
            >
              Join Kempshot Sports Academy
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-gray-200"
            >
              Choose your path and become part of our growing football community
            </motion.p>
          </div>

          {/* Cards Container */}
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            {options.map((option, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                {/* Main Card Image */}
                <div className="relative h-48">
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60" />
                  <img
                    src={option.image}
                    alt={option.title}
                    className="w-full h-full object-cover"
                  />
                  <Link to={option.link} className="absolute bottom-3 left-3 text-white">
                    <div className="flex items-center space-x-2">
                      {option.icon}
                      <h3 className="text-xl font-bold">{option.title}</h3>
                    </div>
                  </Link>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <Link to={option.link} className="block">
                    <p className="text-gray-700 mb-4 text-base">
                      {option.description}
                    </p>
                  </Link>

                  {/* Benefits List */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Key Benefits:</h4>
                    <ul className="space-y-2">
                      {option.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-700 text-sm">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Action Button */}
                  <Link to={option.link} className="block">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center justify-center space-x-2 shadow-md"
                    >
                      <span>Get Started</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </motion.button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom Section */}
          <div className="mt-16 text-center">
            <p className="text-white text-lg">
              Already have an account?{' '}
              <Link to="/auth/sign-in" className="text-blue-400 hover:text-blue-300 font-medium">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Join; 